import styled from '@emotion/styled'
import {
  ExtranetShopProductFamily,
  GetExtranetMaterialProductsQueryDto,
} from '@pharmac-extranet/extranet-api'
import { BannerViewEnum } from '@pharmac-extranet/management-api'
import {
  ExtranetCatalogItemChevron,
  ExtranetContainer,
  ExtranetTab,
  ExtranetTableColumn,
  ExtranetTableGroupColumn,
  ExtranetTableRow,
  ExtranetTabs,
  PharmacSearchWrapper,
  useBreakpoint,
  useDebounce,
  useIntersectionObserver,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacH6,
  PharmacIcon,
  PharmacLabel,
  PharmacLink,
  PharmacLoadingDots,
  PharmacSearch,
  PharmacText,
} from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { createSearchParams, useNavigate } from 'react-router-dom'
import {
  ExtranetCarousel,
  ExtranetCarouselImage,
  ExtranetCarouselItem,
} from '../../components/extranet/ExtranetCarousel'
import { ExtranetViewHeader } from '../../components/extranet/ExtranetViewHeader'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { ExtranetFilters } from '../../components/filters/ExtranetFilters'
import { useShoppingCartContext } from '../../components/navigation/shopping-cart/ShoppingCartContext'
import { NoResultsResponse } from '../../components/responses/NoResultsResponse'
import { ShopOnSaleListItem } from '../../components/shop-products/ShopOnSaleListItem'
import { ShopOwnedListItem } from '../../components/shop-products/ShopOwnedListItem'
import { ShopProductFilters } from '../../components/shop-products/ShopProductFilters'
import { ShopActivateCodeModal } from '../../components/shop-products/activate-code-modal/ShopActivateCodeModal'
import { useShop } from '../../components/shop-products/useShop'
import { mapShopProductSearchParamsToQueryParams } from '../../components/shop-products/util/query-parameter-mapping'
import { useShopProductsQuery } from '../../components/shop-products/util/useShopProductsQuery'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'

const SidebarCategoryTitle = styled(ExtranetTableColumn)`
  font-weight: bold;
  min-height: 0;
`

const SelectedCategory = styled.div<{ isActive?: boolean }>`
  width: 100%;

  ${(props) => props.isActive && 'background-color: #EEE0F1;'};
  ${(props) => props.isActive && 'border-radius: 25px;'};
`

const ActiveteProductButton = styled(PharmacButton)`
  color: var(--color-black);
  background-color: #f3e9f4;
  border-color: #f3e9f4;
`

const ActiveteProductButtonContainer = styled(ExtranetContainer)`
  position: absolute;
  width: 1200px;
  display: flex;
  justify-content: end;
`
const CategoryMobileMenuContainer = styled.div`
  position: relative;
  z-index: 4;
  margin-top: var(--spacing-medium);
  margin-bottom: var(--spacing-medium);
`

const CategoryMobileMenuToggle = styled.div`
  border: 1px solid;
  background-color: var(--color-accent-1);
  color: 'var(--color-primary)';
  max-height: 32px;
  display: flex;
  align-items: center;
  border-top-right-radius: 25px;
  border-top-left-radius: 25px;
`

const CategoryMobileMenuContent = styled.div`
  border: 1px solid;
  background-color: var(--color-accent-1);
  border-bottom-left-radius: 25px;
  border-bottom-right-radius: 25px;
`

export type ShopRootViewProps = {
  activePage?: 'all' | 'own'
}

type MenuItemGroups = {
  name: string
  id: number
}

type DynamicMenuItem = {
  value: string
  name: { fi: string; sv: string; en: string }
  groups: MenuItemGroups[]
}

export const ShopRootView = (props: ShopRootViewProps) => {
  const { minDesktop, maxMobile } = useBreakpoint()
  const { t, i18n, key } = useLanguage()
  const [searchInput, setSearchInput] = useState<string>('')
  const [activateProductCode, setActivateProductCode] = useState<boolean>(false)
  const { customer } = useCustomerContext()
  const { langQuery } = useShop()

  const [selectProductFamily, setSelectProductFamily] = useState<
    ExtranetShopProductFamily | string
  >('')

  const [selectedProductGroup, setSelectedProductGroup] = useState<number | undefined>(undefined)

  const [debouncedParams, setDebouncedParams] = useDebounce(
    {} as GetExtranetMaterialProductsQueryDto
  )

  const navigate = useNavigate()

  const [initialTabIndex, setInitialTabIndex] = useState<number>()

  const createPath = useExtranetPath()

  useEffect(() => {
    const isUsersPage = props.activePage && props.activePage === 'own'
    setInitialTabIndex(isUsersPage ? 1 : 0)
  }, [props.activePage])

  const {
    params,
    searchParams,
    shopProducts,
    shopProductsIsLoading,
    setParams,
    setSearchParams,
    fetchNextPage,
  } = useShopProductsQuery(props.activePage === 'own')

  const shoppingCartHook = useShoppingCartContext()

  const { data: availableProductFamilies } = useQuery({
    queryKey: ['getAvailableProductCategories', customer, props.activePage, i18n.language],
    queryFn: async () =>
      await api.shop.getAvailableShopFamilies(Number(customer?.id), {
        language: langQuery(i18n.language),
        ownProducts: props.activePage === 'all' ? undefined : true,
      }),
    enabled: !!customer?.id,
  })

  const ownProducts = shopProducts?.filter((product) => product.isSubscribed)
  const allProducts = shopProducts?.filter(
    (product) => !product.isSubscribed || (product.unitSellingPrice !== 0 && product.isSubscribed)
  )

  useEffect(() => {
    if (searchInput !== debouncedParams.search) {
      setDebouncedParams((s) => ({ ...s, search: searchInput }))
    }
  }, [searchInput])

  const handleReset = () => {
    setSearchInput('')
    setSearchParams(createSearchParams({}))
  }

  const activeFilterCount = () => {
    return Object.keys(params).filter(
      (param) =>
        param !== 'productFamily' &&
        param !== 'productGroupId' &&
        param !== 'sortOrder' &&
        param !== 'publishDateRangeEnd'
    ).length
  }

  useEffect(() => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          ...debouncedParams,
        })
      )
    )
  }, [debouncedParams])

  const { data: bannersData } = useQuery({
    queryKey: ['__getBanners', i18n.language, BannerViewEnum.Shop],
    queryFn: async () =>
      await api.banners.getAllBanners({ lang: i18n.language, view: BannerViewEnum.Shop }),
  })

  useEffect(() => {
    setParams(() => mapShopProductSearchParamsToQueryParams(searchParams))
  }, [searchParams])

  useEffect(() => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          ...debouncedParams,
        })
      )
    )
  }, [debouncedParams])

  const productCategories = [
    {
      value: '',
      name: {
        fi: t('extranet:shop.wholeCatalog'),
        en: t('extranet:shop.wholeCatalog'),
        sv: t('extranet:shop.wholeCatalog'),
      },
    },
    {
      value: ExtranetShopProductFamily.PhysicalProduct,
      name: {
        fi: t('extranet:shopProducts.relatedProducts'),
        en: t('extranet:shopProducts.relatedProducts'),
        sv: t('extranet:shopProducts.relatedProducts'),
      },
    },
    {
      value: ExtranetShopProductFamily.DocumentProduct,
      name: {
        fi: t('extranet:shopProducts.documents'),
        en: t('extranet:shopProducts.documents'),
        sv: t('extranet:shopProducts.documents'),
      },
    },
    {
      value: ExtranetShopProductFamily.RecordingProduct,
      name: {
        fi: t('extranet:shopProducts.videos'),
        en: t('extranet:shopProducts.videos'),
        sv: t('extranet:shopProducts.videos'),
      },
    },
    {
      value: ExtranetShopProductFamily.ServiceProduct,
      name: {
        fi: t('extranet:shopProducts.serviceProducts'),
        en: t('extranet:shopProducts.serviceProducts'),
        sv: t('extranet:shopProducts.serviceProducts'),
      },
    },
  ]

  const dynamicMenuItems: DynamicMenuItem[] = []

  productCategories.forEach((category) => {
    const notEmptyCategory = availableProductFamilies?.find(
      (it) => it.productFamily === category.value
    )

    if (notEmptyCategory) {
      dynamicMenuItems.push({
        value: category.value,
        name: category.name,
        groups: notEmptyCategory.productGroups.map((group) => {
          return {
            id: group.productGroupId,
            name: group.productGroupName,
          }
        }),
      })
    }
  })

  const handleProductGroupSelection = (groupId: number, category: string) => {
    setSelectProductFamily(category)
    setSelectedProductGroup(groupId)
    setDebouncedParams((s) => ({
      ...s,
      productFamily: category,
      productGroupId: groupId,
    }))
  }

  const handleCategorySelection = (category: string) => {
    setSelectedProductGroup(undefined)
    setSelectProductFamily(category)
    setDebouncedParams((s) => ({
      ...s,
      productFamily: category,
      productGroupId: undefined,
    }))
  }

  const [scrollRef] = useIntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      fetchNextPage()
    }
  })

  const noResults = !shopProductsIsLoading && shopProducts && shopProducts.length === 0
  const noAllProductsResults = !shopProductsIsLoading && allProducts && allProducts.length === 0

  const [open, setOpen] = useState(false)

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        marginSmallTop={!maxMobile}
      >
        <ExtranetCarousel autoplay>
          {(bannersData || []).map((banner) => (
            <ExtranetCarouselItem key={banner.id}>
              <PharmacLink
                href={key(banner.url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExtranetCarouselImage
                  imageSrc={!maxMobile ? key(banner.image) : key(banner.mobileImage)}
                  imageAlt={key(banner.url)}
                />
              </PharmacLink>
            </ExtranetCarouselItem>
          ))}
        </ExtranetCarousel>
      </ExtranetContainer>
      <ExtranetContainer
        full
        flexColumn
        paddingSmallHorizontal={!minDesktop}
        paddingMediumBottom
      >
        <ExtranetContainer
          full
          paddingSmallHorizontal={!minDesktop}
        >
          <ExtranetViewHeader title={t('menu.shop')}>
            <PharmacSearchWrapper>
              <PharmacSearch
                onChange={(v) => setSearchInput(v)}
                value={searchInput}
                placeholder={t('search', { ns: 'common' })}
                searchButtonContent={
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <PharmacIcon
                      icon="search"
                      size="22"
                    />
                  </div>
                }
              />
              <div style={{ width: 'var(--spacing-small)' }} />
              <ExtranetFilters
                activeFilterCount={activeFilterCount()}
                title={t('filters', { ns: 'common' })}
                resetFilters={handleReset}
                hasActiveFilters={!!activeFilterCount()}
                render={({ closeFilters }) => <ShopProductFilters closeFilters={closeFilters} />}
              />
            </PharmacSearchWrapper>
          </ExtranetViewHeader>
        </ExtranetContainer>
      </ExtranetContainer>
      {!minDesktop && (
        <ExtranetContainer
          paddingMediumBottom
          justifyCenter
        >
          <ActiveteProductButton
            fullWidth
            onClick={() => setActivateProductCode(true)}
          >
            {t('extranet:shop.buttons.activeteProduct')}
          </ActiveteProductButton>
        </ExtranetContainer>
      )}
      <ExtranetContainer
        flexRow
        full
      >
        {typeof initialTabIndex === 'number' && (
          <div style={minDesktop ? { flex: 1 } : {}}>
            <ExtranetTabs
              childrenAreAlwaysRendered
              selectedIndex={initialTabIndex}
              tabs={[
                {
                  index: 0,
                  handle: t('extranet:shop.allProducts'),
                  element: (
                    <ExtranetTab
                      padding={minDesktop ? undefined : '0'}
                      style={{ minHeight: minDesktop ? undefined : '1000px' }}
                    >
                      <ExtranetContainer
                        full
                        flexColumn={!minDesktop}
                      >
                        {/* CATEGORY SIDEBAR */}
                        {!maxMobile && (
                          <ExtranetContainer
                            flexColumn
                            paddingMediumTop
                            paddingMediumBottom={!minDesktop}
                            marginLargeBottom={!minDesktop}
                            marginSmallRight={minDesktop}
                            marginLargeLeft={!minDesktop}
                            fifth={minDesktop}
                          >
                            <ExtranetTableRow
                              style={{
                                borderStyle: 'hidden',
                                backgroundColor: 'transparent',
                                color: 'var(--color-primary)',
                                marginBottom: '0',
                              }}
                              toggleExpand={() => null}
                              children={(
                                {
                                  /* isExpanded */
                                }
                              ) => (
                                <>
                                  <ExtranetTableGroupColumn shortHeight>
                                    <ExtranetTableRow>
                                      <SidebarCategoryTitle
                                        shortHeight
                                        onClick={() => handleCategorySelection('')}
                                      >
                                        <ExtranetContainer style={{ cursor: 'pointer' }}>
                                          {key(productCategories[0].name)}
                                        </ExtranetContainer>
                                      </SidebarCategoryTitle>
                                    </ExtranetTableRow>
                                  </ExtranetTableGroupColumn>
                                </>
                              )}
                            />
                            {dynamicMenuItems.map((category) => (
                              <>
                                <>
                                  <ExtranetTableRow
                                    style={{
                                      borderStyle: 'hidden',
                                      backgroundColor: 'transparent',
                                      color: 'var(--color-primary)',
                                      marginBottom: '0',
                                    }}
                                    key={category.value}
                                    expand={
                                      <ExtranetContainer paddingLargeHorizontal>
                                        <ul>
                                          {category.groups.map((group) => {
                                            return (
                                              <SelectedCategory
                                                isActive={selectedProductGroup === group.id}
                                              >
                                                <ExtranetContainer
                                                  style={{
                                                    cursor: 'pointer',
                                                    padding:
                                                      'var(--spacing-tiny) var(--spacing-medium)',
                                                  }}
                                                  onClick={() => {
                                                    handleProductGroupSelection(
                                                      group.id,
                                                      category.value
                                                    )
                                                  }}
                                                >
                                                  <li>{group.name}</li>
                                                </ExtranetContainer>
                                              </SelectedCategory>
                                            )
                                          })}
                                        </ul>
                                      </ExtranetContainer>
                                    }
                                    toggleExpand={() => null}
                                    children={({ isExpanded }) => (
                                      <ExtranetTableGroupColumn
                                        shortHeight
                                        style={{ width: '100%' }}
                                      >
                                        <SelectedCategory
                                          isActive={selectProductFamily === category.value}
                                        >
                                          <ExtranetTableRow>
                                            <SidebarCategoryTitle
                                              onClick={() =>
                                                handleCategorySelection(category.value)
                                              }
                                            >
                                              <ExtranetContainer style={{ cursor: 'pointer' }}>
                                                {key(category.name)}
                                              </ExtranetContainer>
                                            </SidebarCategoryTitle>
                                            <ExtranetContainer paddingSmallRight>
                                              <ExtranetCatalogItemChevron isExpanded={isExpanded} />
                                            </ExtranetContainer>
                                          </ExtranetTableRow>
                                        </SelectedCategory>
                                      </ExtranetTableGroupColumn>
                                    )}
                                  />
                                </>
                              </>
                            ))}
                          </ExtranetContainer>
                        )}

                        {/*  */}

                        <ExtranetContainer
                          style={
                            minDesktop
                              ? {
                                  width: '80%',
                                  borderLeft: '2px solid var(--color-accent-4)',
                                  minHeight: '485px',
                                }
                              : {}
                          }
                          flexRow={minDesktop}
                          flexColumn={!minDesktop}
                          marginMediumHorizontal={!minDesktop}
                          paddingMediumLeft
                        >
                          {/* MOBILE MENU*/}
                          {maxMobile && (
                            <>
                              <CategoryMobileMenuContainer
                                style={{
                                  width: '100%',
                                }}
                              >
                                <CategoryMobileMenuToggle
                                  onClick={() => setOpen(!open)}
                                  style={{
                                    borderBottomLeftRadius: open ? '' : '25px',
                                    borderBottomRightRadius: open ? '' : '25px',
                                  }}
                                >
                                  <PharmacLabel
                                    marginMediumLeft
                                    style={{ color: 'var(--color-primary)' }}
                                  >
                                    {t('common:categories')}
                                  </PharmacLabel>
                                  <ExtranetContainer paddingSmallRight>
                                    <ExtranetCatalogItemChevron />
                                  </ExtranetContainer>
                                </CategoryMobileMenuToggle>
                                <CategoryMobileMenuContent
                                  style={{
                                    opacity: open ? 1 : 0,
                                    maxHeight: open ? 'fit-content' : '38px',
                                    boxShadow: open ? '4px 4px 5px 0 rgba(0, 0, 0, 0.03)' : 'none',
                                    position: 'absolute',
                                    paddingBottom: '10px',
                                    paddingLeft: '10px',
                                    width: '100%',
                                  }}
                                >
                                  <ExtranetTableRow
                                    style={{
                                      borderStyle: 'hidden',
                                      backgroundColor: 'transparent',
                                      color: 'var(--color-primary)',
                                      marginBottom: '0',
                                      marginTop: 'var(--spacing-medium)',
                                    }}
                                  >
                                    <SidebarCategoryTitle
                                      shortHeight
                                      onClick={() => handleCategorySelection('')}
                                    >
                                      {key(productCategories[0].name)}
                                    </SidebarCategoryTitle>
                                  </ExtranetTableRow>

                                  {dynamicMenuItems.map((category) => (
                                    <>
                                      <>
                                        <ExtranetTableRow
                                          style={{
                                            borderStyle: 'hidden',
                                            backgroundColor: 'transparent',
                                            color: 'var(--color-primary)',
                                            marginBottom: '0',
                                          }}
                                          key={category.value}
                                          expand={
                                            <ExtranetContainer paddingLargeHorizontal>
                                              <ul>
                                                {category.groups.map((group) => {
                                                  return (
                                                    <SelectedCategory
                                                      isActive={selectedProductGroup === group.id}
                                                    >
                                                      <ExtranetContainer
                                                        style={{
                                                          padding:
                                                            'var(--spacing-tiny) var(--spacing-medium)',
                                                        }}
                                                        onClick={() =>
                                                          handleProductGroupSelection(
                                                            group.id,
                                                            category.value
                                                          )
                                                        }
                                                      >
                                                        <li> {group.name}</li>
                                                      </ExtranetContainer>
                                                    </SelectedCategory>
                                                  )
                                                })}
                                              </ul>
                                            </ExtranetContainer>
                                          }
                                          toggleExpand={() => null}
                                          children={({ isExpanded }) => (
                                            <>
                                              <ExtranetTableGroupColumn
                                                shortHeight
                                                style={{ width: '100%' }}
                                              >
                                                <SelectedCategory
                                                  isActive={selectProductFamily === category.value}
                                                >
                                                  <ExtranetTableRow>
                                                    <SidebarCategoryTitle
                                                      shortHeight
                                                      onClick={() =>
                                                        handleCategorySelection(category.value)
                                                      }
                                                    >
                                                      {key(category.name)}
                                                    </SidebarCategoryTitle>
                                                    <ExtranetContainer paddingSmallRight>
                                                      <ExtranetCatalogItemChevron
                                                        isExpanded={isExpanded}
                                                      />
                                                    </ExtranetContainer>
                                                  </ExtranetTableRow>
                                                </SelectedCategory>
                                              </ExtranetTableGroupColumn>
                                            </>
                                          )}
                                        />
                                      </>
                                    </>
                                  ))}
                                </CategoryMobileMenuContent>
                              </CategoryMobileMenuContainer>
                            </>
                          )}
                          <ExtranetContainer
                            full
                            flexRow={minDesktop}
                            flexColumn={!minDesktop}
                            flexWrap
                          >
                            <React.Fragment>
                              {shopProductsIsLoading && (
                                <ExtranetContainer
                                  full
                                  justifyCenter
                                >
                                  <PharmacLoadingDots />
                                </ExtranetContainer>
                              )}

                              {!noAllProductsResults &&
                                allProducts?.map((product) => (
                                  <ExtranetContainer
                                    full
                                    key={product.productId}
                                    paddingMediumBottom
                                  >
                                    {product && (
                                      <ShopOnSaleListItem
                                        hook={shoppingCartHook}
                                        key={product.productId}
                                        product={product}
                                      />
                                    )}
                                  </ExtranetContainer>
                                ))}
                            </React.Fragment>
                            {noAllProductsResults && <NoResultsResponse />}
                          </ExtranetContainer>
                        </ExtranetContainer>
                      </ExtranetContainer>
                    </ExtranetTab>
                  ),
                  onClick: () => navigate(createPath('shop')),
                },
                {
                  index: 1,
                  handle: t('extranet:shop.ownProducts'),
                  onClick: () => navigate(createPath('shop.own')),
                  element: (
                    <ExtranetTab
                      padding={minDesktop ? undefined : '0'}
                      style={{ minHeight: minDesktop ? undefined : '1000px' }}
                    >
                      <ExtranetContainer
                        full
                        flexColumn={!minDesktop}
                        style={{ minHeight: '500px' }}
                      >
                        {/* CATEGORY SIDEBAR */}
                        {!maxMobile && (
                          <ExtranetContainer
                            flexColumn
                            paddingMediumTop
                            paddingMediumBottom={!minDesktop}
                            marginLargeBottom={!minDesktop}
                            marginSmallRight={minDesktop}
                            marginLargeLeft={!minDesktop}
                            fourth={minDesktop}
                          >
                            <ExtranetTableRow
                              style={{
                                borderStyle: 'hidden',
                                backgroundColor: 'transparent',
                                color: 'var(--color-primary)',
                                marginBottom: '0',
                              }}
                              toggleExpand={() => null}
                              children={(
                                {
                                  /* isExpanded */
                                }
                              ) => (
                                <>
                                  <ExtranetTableGroupColumn shortHeight>
                                    <ExtranetTableRow>
                                      <SidebarCategoryTitle
                                        shortHeight
                                        onClick={() => handleCategorySelection('')}
                                      >
                                        <ExtranetContainer style={{ cursor: 'pointer' }}>
                                          {key(productCategories[0].name)}
                                        </ExtranetContainer>
                                      </SidebarCategoryTitle>
                                    </ExtranetTableRow>
                                  </ExtranetTableGroupColumn>
                                </>
                              )}
                            />
                            {dynamicMenuItems.map((category) => (
                              <>
                                <>
                                  <ExtranetTableRow
                                    style={{
                                      borderStyle: 'hidden',
                                      backgroundColor: 'transparent',
                                      color: 'var(--color-primary)',
                                      marginBottom: '0',
                                    }}
                                    key={category.value}
                                    expand={
                                      <ExtranetContainer paddingLargeHorizontal>
                                        <ul>
                                          {category.groups.map((group) => {
                                            return (
                                              <SelectedCategory
                                                isActive={selectedProductGroup === group.id}
                                              >
                                                <ExtranetContainer
                                                  style={{
                                                    cursor: 'pointer',
                                                    padding:
                                                      'var(--spacing-tiny) var(--spacing-medium)',
                                                  }}
                                                  onClick={() =>
                                                    handleProductGroupSelection(
                                                      group.id,
                                                      category.value
                                                    )
                                                  }
                                                >
                                                  <li> {group.name}</li>
                                                </ExtranetContainer>
                                              </SelectedCategory>
                                            )
                                          })}
                                        </ul>
                                      </ExtranetContainer>
                                    }
                                    toggleExpand={() => null}
                                    children={({ isExpanded }) => (
                                      <ExtranetTableGroupColumn
                                        shortHeight
                                        style={{ width: '100%' }}
                                      >
                                        <SelectedCategory
                                          isActive={selectProductFamily === category.value}
                                        >
                                          <ExtranetTableRow>
                                            <SidebarCategoryTitle
                                              shortHeight
                                              onClick={() =>
                                                handleCategorySelection(category.value)
                                              }
                                            >
                                              <ExtranetContainer style={{ cursor: 'pointer' }}>
                                                {key(category.name)}
                                              </ExtranetContainer>
                                            </SidebarCategoryTitle>
                                            <ExtranetContainer paddingSmallRight>
                                              <ExtranetCatalogItemChevron isExpanded={isExpanded} />
                                            </ExtranetContainer>
                                          </ExtranetTableRow>
                                        </SelectedCategory>
                                      </ExtranetTableGroupColumn>
                                    )}
                                  />
                                </>
                              </>
                            ))}
                          </ExtranetContainer>
                        )}

                        {/*  */}

                        {/* MOBILE CATEGORY MENU*/}
                        {maxMobile && (
                          <ExtranetContainer
                            full
                            marginMediumHorizontal
                            paddingMediumLeft
                            flexRow
                            flexColumn
                          >
                            <ExtranetContainer
                              full
                              marginMediumRight
                              paddingLargeRight
                            >
                              <CategoryMobileMenuContainer
                                style={{ marginLeft: 'var(--spacing-medium)', width: '100%' }}
                              >
                                <CategoryMobileMenuToggle
                                  onClick={() => setOpen(!open)}
                                  style={{
                                    borderBottomLeftRadius: open ? '' : '25px',
                                    borderBottomRightRadius: open ? '' : '25px',
                                  }}
                                >
                                  <PharmacLabel
                                    marginMediumLeft
                                    style={{ color: 'var(--color-primary)' }}
                                  >
                                    {t('common:categories')}
                                  </PharmacLabel>
                                  <ExtranetContainer paddingSmallRight>
                                    <ExtranetCatalogItemChevron />
                                  </ExtranetContainer>
                                </CategoryMobileMenuToggle>
                                <CategoryMobileMenuContent
                                  style={{
                                    opacity: open ? 1 : 0,
                                    maxHeight: open ? 'fit-content' : '38px',
                                    boxShadow: open ? '4px 4px 5px 0 rgba(0, 0, 0, 0.03)' : 'none',
                                    position: 'absolute',
                                    paddingBottom: '10px',
                                    paddingLeft: '10px',
                                    width: '100%',
                                  }}
                                >
                                  <ExtranetTableRow
                                    style={{
                                      borderStyle: 'hidden',
                                      backgroundColor: 'transparent',
                                      color: 'var(--color-primary)',
                                      marginBottom: '0',
                                      marginTop: 'var(--spacing-medium)',
                                    }}
                                  >
                                    <SidebarCategoryTitle
                                      shortHeight
                                      onClick={() => handleCategorySelection('')}
                                    >
                                      {key(productCategories[0].name)}
                                    </SidebarCategoryTitle>
                                  </ExtranetTableRow>
                                  {dynamicMenuItems.map((category) => (
                                    <>
                                      <>
                                        <ExtranetTableRow
                                          style={{
                                            borderStyle: 'hidden',
                                            backgroundColor: 'transparent',
                                            color: 'var(--color-primary)',
                                            marginBottom: '0',
                                          }}
                                          key={category.value}
                                          expand={
                                            <ExtranetContainer paddingLargeHorizontal>
                                              <ul>
                                                {category.groups.map((group) => {
                                                  return (
                                                    <SelectedCategory
                                                      isActive={selectedProductGroup === group.id}
                                                    >
                                                      <ExtranetContainer
                                                        style={{
                                                          padding:
                                                            'var(--spacing-tiny) var(--spacing-medium)',
                                                        }}
                                                        onClick={() =>
                                                          handleProductGroupSelection(
                                                            group.id,
                                                            category.value
                                                          )
                                                        }
                                                      >
                                                        <li> {group.name}</li>
                                                      </ExtranetContainer>
                                                    </SelectedCategory>
                                                  )
                                                })}
                                              </ul>
                                            </ExtranetContainer>
                                          }
                                          toggleExpand={() => null}
                                          children={({ isExpanded }) => (
                                            <ExtranetTableGroupColumn
                                              shortHeight
                                              style={{ width: '100%' }}
                                            >
                                              <SelectedCategory
                                                isActive={selectProductFamily === category.value}
                                              >
                                                <ExtranetTableRow>
                                                  <SidebarCategoryTitle
                                                    shortHeight
                                                    onClick={() =>
                                                      handleCategorySelection(category.value)
                                                    }
                                                  >
                                                    {key(category.name)}
                                                  </SidebarCategoryTitle>
                                                  <ExtranetContainer paddingSmallRight>
                                                    <ExtranetCatalogItemChevron
                                                      isExpanded={isExpanded}
                                                    />
                                                  </ExtranetContainer>
                                                </ExtranetTableRow>
                                              </SelectedCategory>
                                            </ExtranetTableGroupColumn>
                                          )}
                                        />
                                      </>
                                    </>
                                  ))}
                                </CategoryMobileMenuContent>
                              </CategoryMobileMenuContainer>
                            </ExtranetContainer>
                          </ExtranetContainer>
                        )}
                        {ownProducts.length !== 0 ? (
                          <ExtranetContainer
                            style={
                              minDesktop
                                ? {
                                    width: '100%',
                                    borderLeft: '2px solid var(--color-accent-4)',
                                    minHeight: '485px',
                                  }
                                : {}
                            }
                            flexRow={minDesktop}
                            flexColumn={!minDesktop}
                            marginMediumHorizontal={!minDesktop}
                            paddingMediumLeft
                          >
                            <ExtranetContainer
                              full
                              flexRow={minDesktop}
                              flexColumn={!minDesktop}
                              flexWrap
                            >
                              <React.Fragment>
                                {shopProductsIsLoading && (
                                  <ExtranetContainer
                                    full
                                    justifyCenter
                                  >
                                    <PharmacLoadingDots />
                                  </ExtranetContainer>
                                )}

                                {!noResults &&
                                  ownProducts.map((product) => (
                                    <ExtranetContainer
                                      full
                                      key={product.productId}
                                      paddingMediumBottom
                                    >
                                      {product && (
                                        <ShopOwnedListItem
                                          key={product.productId}
                                          product={product}
                                        />
                                      )}
                                    </ExtranetContainer>
                                  ))}
                              </React.Fragment>
                              {noResults && <NoResultsResponse />}
                            </ExtranetContainer>
                          </ExtranetContainer>
                        ) : (
                          <ExtranetContainer
                            style={
                              minDesktop
                                ? {
                                    width: '100%',
                                    borderLeft: '2px solid var(--color-accent-4)',
                                    minHeight: '485px',
                                  }
                                : {}
                            }
                            flexRow={minDesktop}
                            flexColumn={!minDesktop}
                            marginMediumHorizontal={!minDesktop}
                            paddingMediumLeft
                          >
                            <ExtranetContainer
                              marginMedium
                              paddingLarge
                              style={{
                                border: '2px solid var(--color-accent-4)',
                                borderRadius: '25px',
                              }}
                            >
                              <PharmacH6>{t('extranet:shop.noOwnProducts')}</PharmacH6>
                            </ExtranetContainer>
                          </ExtranetContainer>
                        )}
                      </ExtranetContainer>
                    </ExtranetTab>
                  ),
                },
              ]}
            />
          </div>
        )}
        {minDesktop && (
          <ActiveteProductButtonContainer>
            <ActiveteProductButton onClick={() => setActivateProductCode(true)}>
              <PharmacText weight="semibold">
                {t('extranet:shop.buttons.activeteProduct')}
              </PharmacText>
            </ActiveteProductButton>
          </ActiveteProductButtonContainer>
        )}
      </ExtranetContainer>
      {activateProductCode && (
        <ShopActivateCodeModal
          customerId={Number(customer?.id)}
          activateProductCode={activateProductCode}
          closeModal={() => setActivateProductCode(false)}
        />
      )}
      <div
        ref={scrollRef}
        style={{ height: 10 }}
      />
    </ExtranetWrapper>
  )
}
